import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Sparkles, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { fetchMatch, generatePrediction, getPrediction } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import AdSlot from "@/components/AdSlot";
import MatchStats from "@/components/MatchStats";

export default function MatchDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const [match, setMatch] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => {
    fetchMatch(id).then(setMatch).catch(() => setMatch(null));
    getPrediction(id).then(setPrediction).catch(() => setPrediction(null));
  }, [id]);

  const handleGenerate = async () => {
    setGenLoading(true);
    try {
      const p = await generatePrediction(id);
      setPrediction(p);
      toast.success(t("detail.success"));
    } catch (e) {
      toast.error(t("detail.error"));
    } finally {
      setGenLoading(false);
    }
  };

  if (!match) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 py-20 text-center text-white/40">
        {t("detail.loading")}
      </div>
    );
  }

  const live = match.status === "live";
  const finished = match.status === "finished";
  const upcoming = match.status === "upcoming";

  return (
    <div data-testid="match-detail-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/matches" data-testid="back-to-matches"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-volt">
        <ArrowLeft className="w-4 h-4" /> {t("detail.back")}
      </Link>

      <div className="mt-6 border border-white/10 bg-[#121212] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="relative p-8 lg:p-12">
          <div className="flex items-center justify-between mb-8">
            <div className="text-xs uppercase tracking-[0.22em] text-white/60">{match.league}</div>
            {live && (
              <div className="flex items-center gap-2 px-3 py-1 border border-[#FF3B30]/40 bg-[#FF3B30]/10">
                <span className="live-dot" />
                <span className="text-xs font-mono text-[#FF3B30] font-semibold">LIVE • {match.minute}&apos;</span>
              </div>
            )}
            {finished && <span className="text-xs uppercase tracking-[0.2em] text-white/40">{t("detail.full_time")}</span>}
            {upcoming && (
              <span className="text-xs uppercase tracking-[0.18em] text-white/70 font-mono">
                {new Date(match.kickoff).toLocaleString(lang, {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            )}
          </div>

          <div className="grid grid-cols-3 items-center gap-4">
            <TeamBlock team={match.home_team} />
            <div className="text-center">
              {(live || finished) ? (
                <div className="font-display text-7xl sm:text-9xl tabular-nums score-glow" data-testid="match-score">
                  {match.score_home}<span className="text-white/30 mx-2">-</span>{match.score_away}
                </div>
              ) : (
                <div className="font-display text-5xl sm:text-7xl text-white/30 tabular-nums">{t("match.vs")}</div>
              )}
            </div>
            <TeamBlock team={match.away_team} />
          </div>

          <div className="mt-8 pt-6 border-t border-white/5 flex flex-wrap items-center gap-4 text-xs uppercase tracking-[0.18em] text-white/50">
            <div className="flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> {match.venue}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* MATCH STATS — corners, shots, possession, cards etc */}
          {(live || finished) && (
            <MatchStats matchId={id} homeTeam={match.home_team} awayTeam={match.away_team} />
          )}

          <div className="border border-white/10 bg-[#121212] p-8">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-5 h-5 text-volt" />
            <h2 className="font-display text-3xl uppercase tracking-tighter">{t("detail.ai_prediction")}</h2>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-6">{t("detail.powered_by")}</p>

          {!prediction && !genLoading && (
            <div className="text-center py-10">
              <p className="text-white/60 max-w-md mx-auto mb-6">{t("detail.generate_help")}</p>
              <button
                onClick={handleGenerate}
                data-testid="generate-prediction-btn"
                className="bg-volt text-black font-bold uppercase tracking-[0.18em] px-6 py-3 text-sm hover:bg-[#b4e500] transition-colors"
              >
                {t("detail.generate_cta")}
              </button>
            </div>
          )}

          {genLoading && (
            <div className="text-center py-16 text-white/50 text-sm uppercase tracking-[0.2em]">
              {t("detail.thinking")}
            </div>
          )}

          {prediction && !genLoading && (
            <div className="space-y-6" data-testid="prediction-content">
              <h3 className="font-display text-2xl uppercase tracking-tight text-volt">
                &ldquo;{prediction.headline}&rdquo;
              </h3>

              <div className="space-y-3">
                <ProbabilityRow label={t("detail.home_wins", { team: match.home_team.short })} value={prediction.home_win_pct} testId="prob-home" />
                <ProbabilityRow label={t("detail.draw")} value={prediction.draw_pct} testId="prob-draw" />
                <ProbabilityRow label={t("detail.away_wins", { team: match.away_team.short })} value={prediction.away_win_pct} testId="prob-away" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t("detail.predicted_score")}</div>
                  <div className="font-display text-4xl mt-1 tabular-nums" data-testid="predicted-score">
                    {prediction.predicted_score}
                  </div>
                </div>
                <div className="border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t("detail.confidence")}</div>
                  <div className="font-display text-4xl mt-1 uppercase text-volt">{prediction.confidence}</div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-2">{t("detail.analysis")}</div>
                <p className="text-sm text-white/80 leading-relaxed">{prediction.analysis}</p>
              </div>

              {prediction.key_factors?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-2">{t("detail.key_factors")}</div>
                  <ul className="space-y-2">
                    {prediction.key_factors.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                        <TrendingUp className="w-4 h-4 text-volt shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={handleGenerate}
                data-testid="regenerate-prediction-btn"
                className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-volt"
              >
                {t("detail.regenerate")} →
              </button>
            </div>
          )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-white/10 bg-[#121212] p-6">
            <h3 className="font-display text-xl uppercase tracking-tight mb-3">{t("detail.stadium")}</h3>
            <div className="text-sm text-white/70">{match.venue}</div>
          </div>
          <div className="border border-white/10 bg-[#121212] p-6">
            <h3 className="font-display text-xl uppercase tracking-tight mb-3">{t("detail.competition")}</h3>
            <div className="text-sm text-white/70">{match.league}</div>
          </div>
          <AdSlot
            testId="ad-match-sidebar"
            slot=""
            format="auto"
            style={{ minHeight: 250 }}
          />
        </div>
      </div>
    </div>
  );
}

function TeamBlock({ team }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="w-20 h-20 lg:w-28 lg:h-28 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden mb-3">
        {team.logo_url ? (
          <img src={team.logo_url} alt={team.short} className="w-16 h-16 lg:w-24 lg:h-24 object-contain" />
        ) : (
          <span className="font-mono text-xl lg:text-2xl">{team.logo}</span>
        )}
      </div>
      <div className="font-display text-xl lg:text-3xl uppercase tracking-tight">{team.name}</div>
    </div>
  );
}

function ProbabilityRow({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-sm">
        <span className="uppercase tracking-[0.18em] text-white/70 text-xs">{label}</span>
        <span className="font-display text-xl tabular-nums text-volt">{value}%</span>
      </div>
      <div className="prob-bar mt-1">
        <span style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}
